import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { applyIntakeMapping, IntakeMappingPreview, IntakeMappingResult, previewIntakeMapping } from '../../api/managementReports';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const errorText = (err: unknown) =>
  isAxiosError(err) ? ((err.response?.data as { message?: string } | undefined)?.message ?? 'The clean-up could not be applied.') : 'The clean-up could not be applied.';

type Choice = { include: boolean; month: string; year: string };

/** Intake clean-up (Super Admin): link students' free-text intakes to
 * structured intakes. Preview first — nothing is written until Apply,
 * which the server refuses without a recent completed backup or if the
 * number of students has changed since the preview. */
export const IntakeMappingDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const queryClient = useQueryClient();
  const preview = useQuery({ queryKey: ['management-reports', 'intake-mapping'], queryFn: previewIntakeMapping, enabled: open });
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [reviewed, setReviewed] = useState(false);
  const [result, setResult] = useState<IntakeMappingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!preview.data) return;
    setChoices(
      Object.fromEntries(
        preview.data.labels.map((l) => [
          l.label,
          { include: false, month: l.suggestion ? String(l.suggestion.month) : '', year: l.suggestion ? String(l.suggestion.year) : '' },
        ]),
      ),
    );
    setReviewed(false);
  }, [preview.data]);

  const selected = useMemo(() => (preview.data?.labels ?? []).filter((l) => choices[l.label]?.include), [preview.data, choices]);
  const valid = selected.every((l) => choices[l.label].month && /^\d{4}$/.test(choices[l.label].year));
  // Students whose fees would change are left out by the server; the
  // suggestion only checks against an intake that already exists.
  const expected = (data: IntakeMappingPreview) =>
    data.labels
      .filter((l) => choices[l.label]?.include)
      .reduce((n, l) => {
        const c = choices[l.label];
        const sameAsSuggestion = l.suggestion && String(l.suggestion.month) === c.month && String(l.suggestion.year) === c.year;
        return n + l.students.filter((s) => !(sameAsSuggestion && s.feeScheduleWouldChange)).length;
      }, 0);

  const apply = useMutation({
    mutationFn: () =>
      applyIntakeMapping({
        mappings: selected.map((l) => ({ label: l.label, month: Number(choices[l.label].month), year: Number(choices[l.label].year) })),
        expectedStudents: expected(preview.data!),
      }),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['management-reports'] });
    },
    onError: (err) => setError(errorText(err)),
  });

  const set = (label: string, patch: Partial<Choice>) => setChoices((c) => ({ ...c, [label]: { ...c[label], ...patch } }));
  const data = preview.data;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Intake clean-up</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Students registered with a typed intake (e.g. “February/2027”) and no structured intake can’t be counted in intake reports or towards targets. Choose
          the structured intake for each text below. Only students with no structured intake are changed; the typed text is kept; a missing intake is created in
          Master Data; students whose fee schedule would change are left out. Every change is audited.
        </Typography>
        {preview.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        )}
        {preview.isError && <Alert severity="error">The preview could not be loaded.</Alert>}
        {data && (
          <>
            <Alert severity={data.backupFresh ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {data.backupFresh
                ? `Latest verified backup: ${data.latestBackup!.filename} (${new Date(data.latestBackup!.completedAt!).toLocaleString('en-GB')}).`
                : `Apply needs a completed database backup from the last ${data.backupMaxAgeHours} hours. `}
              {!data.backupFresh && (
                <Button component={RouterLink} to="/database-backups" size="small">
                  Database Backups
                </Button>
              )}
            </Alert>
            {result && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
                {result.updated} student(s) linked to a structured intake.
                {result.createdIntakes.length > 0 && ` Intakes created: ${result.createdIntakes.join(', ')}.`}
                {result.skipped.length > 0 && ` Left out (fee schedule would change): ${result.skipped.map((s) => s.studentNumber).join(', ')}.`} Backup:{' '}
                {result.backup}.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {data.labels.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Every student with a typed intake already has a structured intake.
              </Typography>
            ) : (
              <Table size="small" aria-label="Typed intakes">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 700 }}>Intake as typed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Students
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Structured intake</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Students affected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.labels.map((l) => {
                    const c = choices[l.label];
                    if (!c) return null;
                    return (
                      <TableRow key={l.label} sx={{ verticalAlign: 'top' }}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={c.include} onChange={(e) => set(l.label, { include: e.target.checked })} inputProps={{ 'aria-label': `Map ${l.label}` }} />
                        </TableCell>
                        <TableCell>
                          {l.label}
                          {l.variants.length > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Also written: {l.variants.filter((v) => v !== l.label).join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{l.studentCount}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <TextField select size="small" label="Month" value={c.month} onChange={(e) => set(l.label, { month: e.target.value })} sx={{ width: 140 }}>
                              {MONTHS.map((m, i) => (
                                <MenuItem key={m} value={String(i + 1)}>
                                  {m}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField size="small" label="Year" value={c.year} onChange={(e) => set(l.label, { year: e.target.value })} sx={{ width: 90 }} />
                          </Stack>
                          {l.suggestion ? (
                            <Typography variant="caption" color="text.secondary">
                              Suggested: {l.suggestion.intakeName}
                              {l.suggestion.willCreateIntake ? ' (new intake will be created)' : ' (existing intake)'}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="warning.main">
                              No clear month and year in the text — choose one, or leave it.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {l.students.map((s) => (
                              <Chip
                                key={s.id}
                                size="small"
                                variant="outlined"
                                color={s.feeScheduleWouldChange ? 'warning' : 'default'}
                                label={`${s.studentNumber} ${s.name}${s.feeScheduleWouldChange ? ' — fees would change, left out' : ''}`}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <FormControlLabel
          control={<Checkbox checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />}
          label={data ? `I have reviewed the preview: ${expected(data)} student(s) will be linked.` : 'I have reviewed the preview.'}
          disabled={!selected.length}
        />
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!data || !data.backupFresh || !selected.length || !valid || !reviewed || apply.isPending}
            onClick={() => apply.mutate()}
          >
            Apply
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
